// Production > Dashboard (other UI redesign, handoff W21 — "exception queue":
// organised by action required and risk, with owner and next step).
//
// Two reads:
//   GET /production/tiles — the firm-wide pulse counts (services/
//     production-dashboard.ts; the tiles route lives on the command dashboard
//     router because GET /production/dashboard is the exceptions read).
//   GET /production/jobs  — the open purchase orders with backend-derived
//     blockers + risk (services/production-jobs-rules.ts). The risk/date logic
//     is NEVER re-derived here.
//
// Lives at /production/dashboard; the full table is the Production Report at
// /production/report (the Work queue and Calendar were retired 2026-10-12).
import { useState, type ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { timeAgo } from "@/lib/utils";
import { PageHeader } from "@/components/ui/PageHeader";
import { MetricStrip } from "@/components/ui/RecordParts";
import { BlockerTags, FreshnessNote, RiskLabel, useProductionJobs } from "@/components/production/JobParts";
import {
  BLOCKER_COPY,
  formatShortDate,
  poHref,
  poStageLabel,
  sortByUrgency,
  type ProductionJob,
} from "@/lib/production";

type ProductionPulseSlug =
  | "production-pos-awaiting-ack"
  | "production-late-pos"
  | "production-proofs-awaiting-client"
  | "production-open-exceptions"
  | "production-shipments-this-week"
  | "production-bills-pending-review";

type ProductionBacklogSlug = "production-proofs-awaiting-rep";

interface Kpi<S> {
  slug: S;
  label: string;
  value: number;
  format: "count" | "currency";
  href: string;
  description: string;
}

interface ProductionDashboardResponse {
  pulse: Array<Kpi<ProductionPulseSlug>>;
  backlog: Array<Kpi<ProductionBacklogSlug>>;
  fetchedAt: string;
}

const RUSH_STAGES = new Set(["sent", "confirmed", "in_production"]);
const rushTag = "mr-2 inline-block rounded-sm bg-ink px-1.5 py-0.5 align-middle text-[11px] font-semibold uppercase tracking-wide text-white";

const linkBtn =
  "inline-flex min-h-[40px] shrink-0 items-center rounded-md border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas";

/**
 * Production and admin see the production dashboard; sales reps (client / order
 * rep) see their own POs to send and their F/U Rep POs (Stephanie 2026-10-12).
 * Anyone else goes to the Production Report.
 */
export function ProductionTilesPage() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.roles.some((r) => r === "admin" || r === "production_rep")) return <ProductionDashboard />;
  if (user.roles.some((r) => r === "client_rep" || r === "order_rep")) return <RepProductionDashboard userId={user.sub} />;
  return <Navigate to="/production/report" replace />;
}

/** A sales rep's own POs: they're the project's client rep or order rep. */
const isMine = (j: ProductionJob, userId: string) => j.clientRepId === userId || j.orderRepId === userId;

function RepProductionDashboard({ userId }: { userId: string }) {
  const jobsQ = useProductionJobs("open");
  const mine = (jobsQ.data?.jobs ?? []).filter((j) => isMine(j, userId));
  // Rush first, then soonest in-hands — the same urgency rule as production's view.
  const byUrgency = (a: ProductionJob, b: ProductionJob) =>
    Number(b.rush) - Number(a.rush) || (a.inHandsDate ?? "9999").localeCompare(b.inHandsDate ?? "9999") || a.poNumber - b.poNumber;
  const toSend = mine.filter((j) => j.stage === "draft").sort(byUrgency);
  const followUp = mine.filter((j) => j.productionStatus === "follow_up_rep" && j.stage !== "draft").sort(byUrgency);

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Production / Overview"
        title="Your POs."
        intro="POs waiting to go to the supplier, and the ones production needs you on."
        action={
          <Link to="/production/report" className="inline-flex h-10 items-center rounded-md bg-ink px-[14px] text-sm font-medium text-white hover:bg-ink-700">
            Open the Production Report
          </Link>
        }
      />
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <RepPoList
          id="pos-to-send"
          title="POs to send."
          empty="Every PO on your orders has gone to the supplier."
          jobs={toSend}
          loading={jobsQ.isLoading}
          failed={jobsQ.isError}
          detail={() => "Send the PO to the supplier"}
        />
        <RepPoList
          id="fu-rep"
          title="Production needs you."
          tag={<span className="rounded-sm border border-[#C9AAC9] bg-[#DCC6DC] px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#4A2A4A]">F/U Rep</span>}
          empty="Nothing marked F/U Rep on your orders."
          jobs={followUp}
          loading={jobsQ.isLoading}
          failed={jobsQ.isError}
          detail={(j) => `${poStageLabel(j.stage)}${j.nextActionDate ? ` · next action ${formatShortDate(j.nextActionDate)}` : ""}`}
        />
      </div>
      <FreshnessNote asOf={jobsQ.data?.asOf} updatedAt={jobsQ.dataUpdatedAt} truncated={jobsQ.data?.truncated} />
    </div>
  );
}

function RepPoList({
  id,
  title,
  tag,
  empty,
  jobs,
  loading,
  failed,
  detail,
}: {
  id: string;
  title: string;
  tag?: ReactNode;
  empty: string;
  jobs: ProductionJob[];
  loading: boolean;
  failed: boolean;
  detail: (j: ProductionJob) => string;
}) {
  return (
    <section className="rounded-md border border-rule bg-white p-[22px]" aria-labelledby={id}>
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id={id} className="flex items-center gap-2.5 text-lg font-semibold tracking-[-0.4px] text-ink">
          {title}
          {tag}
        </h2>
        {jobs.length > 0 && (
          <span className="text-xs text-muted">
            {jobs.length.toLocaleString()} {jobs.length === 1 ? "PO" : "POs"}
          </span>
        )}
      </div>
      {loading ? (
        <div className="space-y-3" aria-label="Loading">
          {[0, 1].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : failed ? (
        <p role="alert" className="text-sm text-risk">We couldn't load your POs. Nothing has changed — refresh to try again.</p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-muted">{empty}</p>
      ) : (
        <ul>
          {jobs.map((j) => (
            <li key={j.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-3.5 first:pt-0 last:border-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {j.rush && <span className={rushTag}>Rush</span>}
                  PO #{j.poNumber} · {j.clientName}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {j.vendorName} · {detail(j)}
                  {j.inHandsDate ? ` · in hands ${formatShortDate(j.inHandsDate)}` : ""}
                </p>
              </div>
              <Link to={poHref(j)} className={linkBtn}>
                Open PO #{j.poNumber}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ProductionDashboard() {
  const tiles = useQuery({
    // Own key + path: the work queue (/production) owns "/production/dashboard".
    queryKey: ["production", "tiles"],
    queryFn: () => api<ProductionDashboardResponse>("/production/tiles"),
    refetchOnWindowFocus: true,
  });
  const jobsQ = useProductionJobs("open");

  const pulse = (slug: ProductionPulseSlug): number | null =>
    tiles.data ? (tiles.data.pulse.find((p) => p.slug === slug)?.value ?? 0) : null;

  const jobs = jobsQ.data?.jobs ?? [];
  // POs not sent yet are the sales rep's step (reps send POs, as production),
  // so they stay off production's list (Stephanie 2026-10-12).
  // Proofs out with the client are the sales rep's follow-up too, so they don't
  // count as a production step here (Stephanie 2026-10-12).
  const steps = (j: ProductionJob) => j.blockers.filter((b) => b !== "proof_with_client");
  // Rush orders are high priority: they lead "First things first" (Stephanie 2026-10-12).
  const needsAction = [...jobs]
    .filter((j) => j.stage !== "draft" && steps(j).length > 0)
    .sort((a, b) => Number(b.rush) - Number(a.rush) || sortByUrgency(a, b));
  // Every rush PO with the supplier (sent / confirmed / in production), soonest in-hands first.
  // POs production marked "F/U Vendor" in the report, grouped by supplier so the
  // calls can be batched (Stephanie 2026-10-12). "F/U Rep" stays off on purpose.
  const followUpVendor = jobs
    .filter((j) => j.productionStatus === "follow_up_vendor" && j.stage !== "draft")
    .sort((a, b) => a.vendorName.localeCompare(b.vendorName) || a.poNumber - b.poNumber);
  const rush = jobs
    .filter((j) => j.rush && RUSH_STAGES.has(j.stage))
    .sort((a, b) => (a.inHandsDate ?? "9999").localeCompare(b.inHandsDate ?? "9999") || a.poNumber - b.poNumber);
  // Sent to the supplier over a day ago and still not acknowledged (Stephanie
  // 2026-10-14: "One day works"). Oldest first.
  const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
  const notAcknowledged = jobs
    .filter((j) => j.stage === "sent" && !j.vendorAcknowledgedAt && j.sentAt && Date.parse(j.sentAt) <= dayAgo)
    .sort((a, b) => (a.sentAt ?? "").localeCompare(b.sentAt ?? ""));
  // Early warning (Stephanie 2026-10-14): the server marks POs with the
  // supplier, due to ship within 3 business days, no tracking yet. Soonest first.
  const dueSoon = jobs
    .filter((j) => j.dueToShipInDays != null)
    .sort((a, b) => (a.dueToShipInDays ?? 0) - (b.dueToShipInDays ?? 0) || a.poNumber - b.poNumber);
  const top = needsAction.slice(0, 7);
  const count = (b: ProductionJob["blockers"][number]) => jobs.filter((j) => j.blockers.includes(b)).length;

  const late = pulse("production-late-pos");
  const exceptions = pulse("production-open-exceptions");
  const unconfirmed = pulse("production-pos-awaiting-ack");
  // No count of proofs out with clients: the sales rep follows those up (Stephanie 2026-10-12).

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Production / Overview"
        title="Let's keep things moving."
        intro="The blockers, the deadlines, and who has the next step."
        action={
          <Link to="/production/report" className="inline-flex h-10 items-center rounded-md bg-ink px-[14px] text-sm font-medium text-white hover:bg-ink-700">
            Open the Production Report
          </Link>
        }
      />

      <RushOrders jobs={rush} loading={jobsQ.isLoading} failed={jobsQ.isError} steps={steps} />

      <ShippingExceptions />

      <FollowUpVendor jobs={followUpVendor} loading={jobsQ.isLoading} failed={jobsQ.isError} />

      <NotAcknowledged jobs={notAcknowledged} loading={jobsQ.isLoading} failed={jobsQ.isError} />

      <DueToShipSoon jobs={dueSoon} />

      {tiles.isError ? (
        <div role="alert" className="mb-7 rounded-md border border-risk/30 bg-risk-bg p-4 text-sm text-risk">
          We couldn't load the production counts. Nothing has changed — refresh to try again.
        </div>
      ) : (
        <div className="mb-7">
          <MetricStrip
            ariaLabel="Production summary"
            cells={[
              { label: "Late purchase orders", value: late, note: "Past the required ship date", attention: (late ?? 0) > 0 },
              { label: "Open exceptions", value: exceptions, note: "Damage, wrong art, supplier issues", attention: (exceptions ?? 0) > 0 },
              { label: "Not confirmed by supplier", value: unconfirmed, note: "Sent, no acknowledgment yet" },
            ]}
          />
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(260px,1fr)]">
        <section className="rounded-md border border-rule bg-white p-[22px]" aria-labelledby="first-things">
          <div className="mb-3.5 flex items-baseline justify-between gap-3">
            <h2 id="first-things" className="text-lg font-semibold tracking-[-0.4px] text-ink">First things first.</h2>
            {needsAction.length > 0 && (
              <span className="text-xs text-muted">
                {needsAction.length.toLocaleString()} {needsAction.length === 1 ? "job needs" : "jobs need"} a step
              </span>
            )}
          </div>
          {jobsQ.isLoading ? (
            <div className="space-y-3" aria-label="Loading jobs">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-canvas" />
              ))}
            </div>
          ) : jobsQ.isError ? (
            <p role="alert" className="text-sm text-risk">We couldn't load open jobs. Nothing has changed — refresh to try again.</p>
          ) : top.length === 0 ? (
            <p className="text-sm text-muted">
              Nothing is blocked. Every open purchase order is sent, confirmed and on time.
            </p>
          ) : (
            <ul>
              {top.map((j) => (
                <li key={j.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-4 first:pt-0 last:border-0 last:pb-0">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ink">
                      {j.rush && <span className={rushTag}>Rush</span>}
                      {BLOCKER_COPY[steps(j)[0]!].next}
                      <span className="ml-2 align-middle"><RiskLabel risk={j.risk} /></span>
                    </p>
                    <p className="mt-1 text-xs text-muted">
                      PO #{j.poNumber} · {j.vendorName} · {j.clientName}
                      {j.inHandsDate ? ` · in hands ${formatShortDate(j.inHandsDate)}` : ""}
                      {j.ownerName ? ` · ${j.ownerName}` : " · no owner"}
                    </p>
                    {steps(j).length > 1 && (
                      <div className="mt-2">
                        <BlockerTags blockers={steps(j).slice(1)} />
                      </div>
                    )}
                  </div>
                  <Link to={poHref(j)} className={linkBtn}>
                    Open PO #{j.poNumber}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {needsAction.length > top.length && (
            <Link to="/production/report" className="mt-4 inline-block text-sm text-ink underline underline-offset-4 hover:text-muted">
              See all {needsAction.length.toLocaleString()} in the Production Report
            </Link>
          )}
        </section>

        <section className="rounded-md border border-rule bg-white p-[22px]" aria-labelledby="this-week">
          <h2 id="this-week" className="mb-3.5 text-lg font-semibold tracking-[-0.4px] text-ink">This week.</h2>
          <dl className="divide-y divide-[#e8e8e5] text-sm">
            <WeekRow label="In hands within 7 days" value={jobsQ.data ? count("in_hands_soon") : null} to="/production/report?blocker=in_hands_soon" />
            <WeekRow label="Blanks not at the decorator" value={jobsQ.data ? count("blanks_not_arrived") : null} to="/production/report?blocker=blanks_not_arrived" warn />
            <WeekRow label="Tracking due to leave" value={pulse("production-shipments-this-week")} to="/production/report?sort=next_action" />
            <WeekRow label="Supplier bills to review" value={pulse("production-bills-pending-review")} to="/finance/billing?status=pending_review" />
          </dl>
        </section>
      </div>

      <FreshnessNote asOf={jobsQ.data?.asOf} updatedAt={jobsQ.dataUpdatedAt} truncated={jobsQ.data?.truncated} />
    </div>
  );
}

function WeekRow({ label, value, to, warn }: { label: string; value: number | null; to: string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <dt>
        <Link to={to} className="text-ink hover:underline">{label}</Link>
      </dt>
      <dd className={warn && (value ?? 0) > 0 ? "font-medium tabular-nums text-warn" : "font-medium tabular-nums text-ink"}>
        {value === null ? "—" : value.toLocaleString()}
      </dd>
    </div>
  );
}

/** Rush orders — high priority, so they sit at the top in the report's rush black (Stephanie 2026-10-12). */
function RushOrders({
  jobs,
  loading,
  failed,
  steps,
}: {
  jobs: ProductionJob[];
  loading: boolean;
  failed: boolean;
  steps: (j: ProductionJob) => ProductionJob["blockers"];
}) {
  return (
    <section className="mb-7 overflow-hidden rounded-md border border-ink bg-white" aria-labelledby="rush-orders">
      <div className="flex items-baseline justify-between gap-3 bg-ink px-[22px] py-3.5 text-white">
        <h2 id="rush-orders" className="text-lg font-semibold tracking-[-0.4px] text-white">Rush orders.</h2>
        {jobs.length > 0 && (
          <span className="text-xs text-white/70">
            {jobs.length.toLocaleString()} with {jobs.length === 1 ? "a supplier" : "suppliers"}
          </span>
        )}
      </div>
      {loading ? (
        <div className="space-y-3 p-[22px]" aria-label="Loading rush orders">
          {[0, 1].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : failed ? (
        <p role="alert" className="p-[22px] text-sm text-risk">We couldn't load rush orders. Nothing has changed — refresh to try again.</p>
      ) : jobs.length === 0 ? (
        <p className="p-[22px] text-sm text-muted">No rush orders with suppliers right now.</p>
      ) : (
        <ul className="px-[22px]">
          {jobs.map((j) => {
            const next = steps(j)[0];
            return (
              <li key={j.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-4 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">
                    PO #{j.poNumber} · {j.clientName}
                    {j.firmInHands && <span className="ml-2 inline-block rounded-sm border border-ink px-1.5 py-0.5 align-middle text-[11px] font-semibold uppercase tracking-wide text-ink">Firm</span>}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {j.vendorName} · {poStageLabel(j.stage)}
                    {j.inHandsDate ? ` · in hands ${formatShortDate(j.inHandsDate)}` : " · no in-hands date"}
                    {j.ownerName ? ` · ${j.ownerName}` : " · no owner"}
                  </p>
                  <p className="mt-1 text-sm text-ink">
                    {next ? BLOCKER_COPY[next].next : "On track — nothing to do yet"}
                    <span className="ml-2 align-middle"><RiskLabel risk={j.risk} /></span>
                  </p>
                </div>
                <Link to={poHref(j)} className={linkBtn}>
                  Open PO #{j.poNumber}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface ShippingExceptionRow {
  shipmentId: string;
  poId: string;
  poNumber: number;
  projectId: string;
  projectName: string;
  clientName: string;
  supplier: string;
  carrier: string | null;
  trackingNumber: string | null;
  reason: string | null;
  since: string | null;
  inHandsDate: string | null;
}

const CARRIER_NAME: Record<string, string> = { ups: "UPS", fedex: "FedEx", usps: "USPS", dhl: "DHL", freight: "Freight" };

/**
 * Packages the carrier flagged (live tracking, Stephanie 2026-10-13). Flow
 * checks UPS / FedEx / USPS / DHL every few hours, emails production on a new
 * exception, and lists them here until the carrier clears them. Shown only
 * when there is something to look at.
 */
function ShippingExceptions() {
  const q = useQuery({
    queryKey: ["shipments", "exceptions"],
    queryFn: () => api<{ rows: ShippingExceptionRow[] }>("/shipments/exceptions"),
    refetchOnWindowFocus: true,
  });
  const rows = q.data?.rows ?? [];
  if (q.isLoading || q.isError || rows.length === 0) return null;
  return (
    <section className="mb-7 overflow-hidden rounded-md border border-risk/40 bg-white" aria-labelledby="ship-exceptions">
      <div className="flex items-baseline justify-between gap-3 border-b border-risk/20 bg-risk-bg px-[22px] py-3.5">
        <h2 id="ship-exceptions" className="text-lg font-semibold tracking-[-0.4px] text-risk">Shipping exceptions.</h2>
        <span className="text-xs text-risk">
          {rows.length} {rows.length === 1 ? "package" : "packages"} flagged by the carrier
        </span>
      </div>
      <ul className="px-[22px]">
        {rows.map((r) => (
          <li key={r.shipmentId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-4 last:border-0">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ink">PO #{r.poNumber} · {r.clientName}</p>
              <p className="mt-1 text-sm text-ink">{r.reason ?? "Exception — no reason given"}</p>
              <p className="mt-1 text-xs text-muted">
                {r.carrier ? (CARRIER_NAME[r.carrier] ?? r.carrier) : "Carrier"}
                {r.trackingNumber ? ` ${r.trackingNumber}` : ""} · {r.supplier}
                {r.inHandsDate ? ` · in hands ${formatShortDate(r.inHandsDate)}` : ""}
                {r.since ? ` · flagged ${formatShortDate(r.since.slice(0, 10))}` : ""}
              </p>
            </div>
            <Link to={`/projects/${r.projectId}/pos/${r.poId}`} className={linkBtn}>
              Open PO #{r.poNumber}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** POs marked "F/U Vendor" in the Production Report — the supplier needs a call (Stephanie 2026-10-12). */
/** "due today" / "1 business day" / "3 business days". */
function shipsIn(days: number): string {
  return days <= 0 ? "due today" : days === 1 ? "due in 1 business day" : `due in ${days} business days`;
}

/** POs due to ship within 3 days with no tracking yet — chase before late. */
function DueToShipSoon({ jobs }: { jobs: ProductionJob[] }) {
  if (jobs.length === 0) return null;
  return (
    <section className="mb-7 rounded-md border border-rule bg-white p-[22px]" aria-labelledby="due-soon">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id="due-soon" className="text-lg font-semibold tracking-[-0.4px] text-ink">Due to ship soon.</h2>
        <span className="text-xs text-muted">
          {jobs.length.toLocaleString()} {jobs.length === 1 ? "PO" : "POs"} · next 3 business days, no tracking yet
        </span>
      </div>
      <ul>
        {jobs.map((j) => (
          <li key={j.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-3.5 first:pt-0 last:border-0 last:pb-0">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ink">
                {j.vendorName}
                {" · "}PO #{j.poNumber}
                {j.rush && <span className={`${rushTag} ml-2`}>Rush</span>}
              </p>
              <p className="mt-1 text-xs text-muted">
                <span className="font-semibold text-warn">{shipsIn(j.dueToShipInDays ?? 0)}</span>
                {` (${formatShortDate(j.requiredShipDate)})`} · {j.clientName} · {poStageLabel(j.stage)}
                {j.inHandsDate ? ` · in hands ${formatShortDate(j.inHandsDate)}` : ""}
              </p>
            </div>
            <Link to={poHref(j)} className={linkBtn}>
              Open PO #{j.poNumber}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** POs the supplier hasn't acknowledged a day after sending, with a one-click
 *  reminder (re-sends the PO to the supplier, "Reminder:" subject). */
function NotAcknowledged({ jobs, loading, failed }: { jobs: ProductionJob[]; loading: boolean; failed: boolean }) {
  const queryClient = useQueryClient();
  const [sent, setSent] = useState<Record<string, "sending" | "sent" | string>>({});
  const remind = useMutation({
    mutationFn: (poId: string) =>
      api(`/pos/${poId}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reminder: true }),
      }),
    onMutate: (poId) => setSent((m) => ({ ...m, [poId]: "sending" })),
    onSuccess: (_d, poId) => {
      setSent((m) => ({ ...m, [poId]: "sent" }));
      void queryClient.invalidateQueries({ queryKey: ["production"] });
    },
    onError: (e, poId) =>
      setSent((m) => ({
        ...m,
        [poId]:
          e instanceof ApiError
            ? ((e.body as { message?: string; error?: string } | null)?.message ??
              (e.body as { error?: string } | null)?.error ??
              "Couldn't send the reminder.")
            : "Couldn't send the reminder.",
      })),
  });
  if (!loading && !failed && jobs.length === 0) return null;
  return (
    <section className="mb-7 rounded-md border border-rule bg-white p-[22px]" aria-labelledby="not-acked">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id="not-acked" className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Not acknowledged after a day.
        </h2>
        {jobs.length > 0 && (
          <span className="text-xs text-muted">
            {jobs.length.toLocaleString()} {jobs.length === 1 ? "PO" : "POs"}
          </span>
        )}
      </div>
      {loading ? (
        <div className="h-10 animate-pulse rounded bg-canvas" aria-label="Loading" />
      ) : failed ? (
        <p role="alert" className="text-sm text-risk">We couldn't load these. Nothing has changed — refresh to try again.</p>
      ) : (
        <ul>
          {jobs.map((j) => {
            const state = sent[j.id];
            return (
              <li key={j.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-3.5 first:pt-0 last:border-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-ink">
                    {j.vendorName}
                    {" · "}PO #{j.poNumber}
                    {j.rush && <span className={`${rushTag} ml-2`}>Rush</span>}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {j.clientName} · sent {timeAgo(j.sentAt)}
                    {j.inHandsDate ? ` · in hands ${formatShortDate(j.inHandsDate)}` : ""}
                  </p>
                  {state && state !== "sending" && state !== "sent" && (
                    <p role="alert" className="mt-1 text-xs text-risk">{state}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {state === "sent" ? (
                    <span className="text-sm font-medium text-ok">Reminder sent</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => remind.mutate(j.id)}
                      disabled={state === "sending"}
                      className={linkBtn}
                    >
                      {state === "sending" ? "Sending…" : "Send reminder"}
                    </button>
                  )}
                  <Link to={poHref(j)} className={linkBtn}>
                    Open PO #{j.poNumber}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function FollowUpVendor({ jobs, loading, failed }: { jobs: ProductionJob[]; loading: boolean; failed: boolean }) {
  return (
    <section className="mb-7 rounded-md border border-rule bg-white p-[22px]" aria-labelledby="fu-vendor">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id="fu-vendor" className="flex items-center gap-2.5 text-lg font-semibold tracking-[-0.4px] text-ink">
          Follow up with suppliers.
          <span className="rounded-sm border border-[#B9CCDD] bg-[#D5E1EC] px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#2F4A63]">F/U Vendor</span>
        </h2>
        {jobs.length > 0 && (
          <span className="text-xs text-muted">
            {jobs.length.toLocaleString()} {jobs.length === 1 ? "PO" : "POs"}
          </span>
        )}
      </div>
      {loading ? (
        <div className="space-y-3" aria-label="Loading follow-ups">
          {[0, 1].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : failed ? (
        <p role="alert" className="text-sm text-risk">We couldn't load follow-ups. Nothing has changed — refresh to try again.</p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-muted">No POs marked F/U Vendor in the Production Report.</p>
      ) : (
        <ul>
          {jobs.map((j) => (
            <li key={j.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-3.5 first:pt-0 last:border-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {j.vendorName}
                  {" · "}PO #{j.poNumber}
                  {j.rush && <span className={`${rushTag} ml-2`}>Rush</span>}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {j.clientName} · {poStageLabel(j.stage)}
                  {j.nextActionDate ? ` · next action ${formatShortDate(j.nextActionDate)}` : ""}
                  {j.inHandsDate ? ` · in hands ${formatShortDate(j.inHandsDate)}` : ""}
                  {j.ownerName ? ` · ${j.ownerName}` : ""}
                </p>
              </div>
              <Link to={poHref(j)} className={linkBtn}>
                Open PO #{j.poNumber}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
